import {
  APP_INITIALIZER,
  ApplicationConfig,
  Injector,
  LOCALE_ID,
  importProvidersFrom,
} from "@angular/core";

import { appRoutes } from "@/app.routes";
import { registerLocaleData } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import localeFr from "@angular/common/locales/fr";
import { createCustomElement } from "@angular/elements";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from "@angular/material-moment-adapter";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatBadgeModule } from "@angular/material/badge";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import {
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
  MatNativeDateModule,
  MatOptionModule,
} from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogModule,
} from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
} from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import {
  MatPaginatorIntl,
  MatPaginatorModule,
} from "@angular/material/paginator";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatSortModule } from "@angular/material/sort";
import { MatStepperModule } from "@angular/material/stepper";
import { MatTableModule } from "@angular/material/table";
import { MatTabsModule } from "@angular/material/tabs";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  BrowserModule,
  HAMMER_GESTURE_CONFIG,
} from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, withComponentInputBinding } from "@angular/router";
import { LeafletModule } from "@asymmetrik/ngx-leaflet";
import { ApiModule, BASE_PATH } from "@eemo/api-client";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgxLeafletLocateModule } from "@runette/ngx-leaflet-locate";
import { GoogleChartsModule } from "angular-google-charts";
import { MdbCarouselModule } from "mdb-angular-ui-kit/carousel";
import { MdbPopoverModule } from "mdb-angular-ui-kit/popover";
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from "ng-recaptcha";
import { PdfJsViewerModule } from "ng2-pdfjs-viewer";
import { CookieService } from "ngx-cookie-service";
import { FileSaverModule } from "ngx-filesaver";
import { getFrenchPaginatorIntl } from "../app/extensions/frenchPaginatorIntl";
import { MobileGestureConfig } from "../app/extensions/mobileGestureConfig";
import { ErrorInterceptor } from "../app/handlers/error.interceptor";
import { LoaderInterceptor } from "../app/handlers/loader.interceptor";
import { getApiBaseUrl } from "../app/helpers/app.constants";
import { IconEemoModuleModule } from "../app/modules/icon-eemo-module/icon-eemo-module.module";
import { MapModule } from "../app/modules/map/map.module";
import { environment } from "../environments/environment";
import { PropertyCardComponent } from "./components/property-card/property-card.component";
import { VersionCheckService } from "./services/version-check.service";

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  // add the haash in query string to avoid cache issues
  return new TranslateHttpLoader(
    http,
    "./assets/i18n/",
    ".json?" + VersionCheckService.codeHash
  );
}

export const MY_FORMAT: MatDateFormats = {
  parse: {
    dateInput: "DD/MM/YYYY",
  },
  display: {
    dateInput: "DD/MM/YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "DD/MM/YYYY",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

registerLocaleData(localeFr, "fr");

export function registerCustomElements(injector: Injector): void {
  const PropertyCardElement = createCustomElement(PropertyCardComponent, {
    injector,
  });
  customElements.define("property-card-element", PropertyCardElement);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withComponentInputBinding()),
    importProvidersFrom(
      BrowserModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: "fr",
      }),
      ApiModule,
      FormsModule,
      ReactiveFormsModule,
      LeafletModule,
      NgxLeafletLocateModule,
      GoogleChartsModule,
      RecaptchaV3Module,
      MatButtonModule,
      MatMenuModule,
      MatToolbarModule,
      MatIconModule,
      MatCardModule,
      MatFormFieldModule,
      MatInputModule,
      MatDatepickerModule,
      MatNativeDateModule,
      MatRadioModule,
      MatCheckboxModule,
      MatSelectModule,
      MatOptionModule,
      MatSlideToggleModule,
      MatProgressSpinnerModule,
      MatSidenavModule,
      MatListModule,
      MatDialogModule,
      MatTooltipModule,
      MatBadgeModule,
      MatTabsModule,
      MatTableModule,
      MatPaginatorModule,
      MatSortModule,
      MatExpansionModule,
      MatProgressBarModule,
      MatAutocompleteModule,
      MatSnackBarModule,
      MatChipsModule,
      MatStepperModule,
      IconEemoModuleModule,
      FileSaverModule,
      PdfJsViewerModule,
      // Bootstrap ui-kit
      MdbPopoverModule,
      MdbCarouselModule,
      MapModule
    ),
    { provide: BASE_PATH, useValue: getApiBaseUrl() },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: "fill" },
    },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
    { provide: MatPaginatorIntl, useValue: getFrenchPaginatorIntl() },
    { provide: LOCALE_ID, useValue: "fr" },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: MobileGestureConfig,
    },
    CookieService,
    { provide: MAT_DATE_LOCALE, useValue: "fr-FR" },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMAT },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaSiteKey },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    {
      provide: APP_INITIALIZER,
      useFactory: (injector: Injector) => () =>
        registerCustomElements(injector),
      deps: [Injector],
      multi: true,
    },
  ],
};
